/*
 * 1-Minute Maths
 * Copyright © 2021 - 2022 White Rose Maths
 * useRefLazy.ts - Convenience hook for initializing refs lazily
 */

import { MutableRefObject, useRef } from 'react';

/** Represents the state where the ref is not yet initialized. Not exported. */
const uninitialized = Symbol('uninitialized');

/**
 * Like useRef, but can be passed an initialization function which is only run on first render to calculate the 
 * initial value.
 * 
 * Compare:
 * 
 * 1. `useRef(foo())`
 * 2. `useRefLazy(() => foo())` or equivalently `useRefLazy(foo)`
 * 
 * In the former, `foo` is executed on every render, and its value is discarded on all renders but the first.
 * In the latter, `foo` is executed only on the first render.
 */
const useRefLazy = <T> (initializer: () => T) => {
  const ref = useRef<T | typeof uninitialized>(uninitialized);
  if (ref.current === uninitialized) {
    ref.current = initializer();
  }
  return ref as MutableRefObject<T>;
}; 

export default useRefLazy;
